import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { RefreshCw, Search, TrendingUp } from 'lucide-react'
import { getBookBrandColor, getBookLogo } from '../lib/logoMaps'
import {
  buildMovementCatalog,
  fetchMovementBundle,
  impliedProbability,
} from '../lib/movementData'
import './LineMovementWidget.css'

const REFRESH_MS = 60_000
const STALE_AFTER_MS = 5 * 60_000
const MARKET_LABELS = { ML: 'Moneyline', SPREAD: 'Spread / Run Line', TOTAL: 'Total' }

function formatOdds(american) {
  if (!Number.isFinite(Number(american))) return '—'
  return Number(american) > 0 ? `+${american}` : String(american)
}

function formatProbabilityMove(opening, current) {
  const openProbability = impliedProbability(opening)
  const currentProbability = impliedProbability(current)
  if (openProbability == null || currentProbability == null) return { label: '—', value: 0 }
  const value = (currentProbability - openProbability) * 100
  return { label: `${value > 0 ? '+' : ''}${value.toFixed(1)}pp`, value }
}

function drawMovementChart(canvas, series) {
  const context = canvas.getContext('2d')
  const rect = canvas.parentElement.getBoundingClientRect()
  const dpr = window.devicePixelRatio || 1
  const width = Math.max(320, rect.width)
  const height = 280
  const padding = { top: 20, right: 20, bottom: 34, left: 52 }
  const points = series.flatMap((entry) => entry.points.map((point) => ({
    ...point,
    probability: impliedProbability(point.americanOdds),
  })).filter((point) => point.probability != null))

  canvas.width = width * dpr
  canvas.height = height * dpr
  canvas.style.width = `${width}px`
  canvas.style.height = `${height}px`
  context.scale(dpr, dpr)
  context.fillStyle = '#07111f'
  context.fillRect(0, 0, width, height)
  if (points.length === 0) return

  const times = points.map((point) => Date.parse(point.timestamp))
  const probabilities = points.map((point) => point.probability)
  const minTime = Math.min(...times)
  const maxTime = Math.max(...times)
  const rawMin = Math.min(...probabilities)
  const rawMax = Math.max(...probabilities)
  const probabilityPad = Math.max(0.01, (rawMax - rawMin) * 0.12)
  const minProbability = Math.max(0, rawMin - probabilityPad)
  const maxProbability = Math.min(1, rawMax + probabilityPad)
  const timeRange = maxTime - minTime || 1
  const probabilityRange = maxProbability - minProbability || 1
  const plotWidth = width - padding.left - padding.right
  const plotHeight = height - padding.top - padding.bottom

  context.font = '10px ui-monospace, monospace'
  for (let index = 0; index <= 4; index += 1) {
    const ratio = index / 4
    const y = padding.top + ratio * plotHeight
    context.strokeStyle = 'rgba(119, 155, 181, 0.12)'
    context.beginPath()
    context.moveTo(padding.left, y)
    context.lineTo(width - padding.right, y)
    context.stroke()
    context.fillStyle = '#667b90'
    context.textAlign = 'right'
    context.fillText(`${((maxProbability - ratio * probabilityRange) * 100).toFixed(1)}%`, padding.left - 7, y + 3)
  }

  const timeSteps = Math.min(5, Math.max(2, Math.floor(plotWidth / 110)))
  for (let index = 0; index <= timeSteps; index += 1) {
    const ratio = index / timeSteps
    const timestamp = minTime + ratio * timeRange
    const x = padding.left + ratio * plotWidth
    context.fillStyle = '#667b90'
    context.textAlign = 'center'
    context.fillText(new Date(timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }), x, height - 10)
  }

  series.forEach((entry) => {
    const validPoints = entry.points
      .map((point) => ({ ...point, probability: impliedProbability(point.americanOdds) }))
      .filter((point) => point.probability != null)
    if (validPoints.length === 0) return
    context.strokeStyle = getBookBrandColor(entry.bookmaker) || '#8aa6bd'
    context.lineWidth = 1.8
    context.beginPath()
    validPoints.forEach((point, index) => {
      const x = padding.left + ((Date.parse(point.timestamp) - minTime) / timeRange) * plotWidth
      const y = padding.top + ((maxProbability - point.probability) / probabilityRange) * plotHeight
      if (index === 0) context.moveTo(x, y)
      else context.lineTo(x, y)
    })
    context.stroke()
  })
}

export default function LineMovementWidget() {
  const [catalog, setCatalog] = useState({ games: [], series: [], proof: null })
  const [selectedGameId, setSelectedGameId] = useState('')
  const [league, setLeague] = useState('ALL')
  const [market, setMarket] = useState('ML')
  const [search, setSearch] = useState('')
  const [showAllLines, setShowAllLines] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const canvasRef = useRef(null)

  const refresh = useCallback(async () => {
    try {
      setLoading(true)
      const bundle = await fetchMovementBundle()
      setCatalog(buildMovementCatalog(bundle))
      setError(null)
    } catch (fetchError) {
      setError(fetchError.message || 'Line movement data unavailable')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
    const interval = setInterval(refresh, REFRESH_MS)
    return () => clearInterval(interval)
  }, [refresh])

  const leagues = useMemo(
    () => ['ALL', ...Array.from(new Set(catalog.games.map((game) => game.league))).sort()],
    [catalog.games]
  )
  const visibleGames = useMemo(() => {
    const query = search.trim().toLowerCase()
    return catalog.games.filter((game) => (
      (league === 'ALL' || game.league === league)
      && (!query || game.event.toLowerCase().includes(query))
    ))
  }, [catalog.games, league, search])
  const selectedGame = useMemo(
    () => catalog.games.find((game) => game.id === selectedGameId) || null,
    [catalog.games, selectedGameId]
  )

  useEffect(() => {
    if (!visibleGames.some((game) => game.id === selectedGameId)) {
      setSelectedGameId(visibleGames[0]?.id || '')
    }
  }, [visibleGames, selectedGameId])

  useEffect(() => {
    if (selectedGame && !selectedGame.markets.includes(market)) {
      setMarket(selectedGame.markets[0] || 'ML')
    }
  }, [selectedGame, market])

  const visibleSeries = useMemo(() => catalog.series
    .filter((entry) => entry.gameId === selectedGameId && entry.marketCode === market)
    .filter((entry) => showAllLines || entry.marketCode === 'ML' || entry.isPrimary)
    .sort((left, right) => left.bookmaker.localeCompare(right.bookmaker)
      || left.selection.localeCompare(right.selection)
      || Number(left.line || 0) - Number(right.line || 0)),
  [catalog.series, market, selectedGameId, showAllLines])

  useEffect(() => {
    if (!canvasRef.current || visibleSeries.length === 0) return
    drawMovementChart(canvasRef.current, visibleSeries)
  }, [visibleSeries])

  const proofAgeMs = catalog.proof?.capturedAt
    ? Math.max(0, Date.now() - Date.parse(catalog.proof.capturedAt))
    : null
  const proofIsStale = proofAgeMs == null || proofAgeMs > STALE_AFTER_MS

  return (
    <div className="widget lmw-widget">
      <div className="widget-header">
        <h3 className="widget-title">
          <TrendingUp size={16} />
          Line Movement
          <span className="lmw-count">{catalog.proof?.historyOffers?.toLocaleString() || 0} tapes</span>
        </h3>
        <button className="widget-refresh-btn" onClick={refresh} title="Refresh">
          <RefreshCw size={14} className={loading ? 'lmw-spinner' : ''} />
        </button>
      </div>

      {catalog.proof && (
        <div className={`lmw-proof ${proofIsStale ? 'is-stale' : 'is-live'}`}>
          <span>Cloudflare board release</span>
          <span>{catalog.proof.bookCount} books</span>
          <span>{catalog.proof.boardRows.toLocaleString()} current rows</span>
          <span>{catalog.proof.openerOffers.toLocaleString()} openers</span>
          <span>{catalog.proof.closerOffers.toLocaleString()} closers</span>
          <span>Captured {new Date(catalog.proof.capturedAt).toLocaleString()}</span>
          <span>{Math.round(proofAgeMs / 60_000)}m old</span>
        </div>
      )}

      <div className="lmw-filters">
        <label>
          League
          <select value={league} onChange={(event) => setLeague(event.target.value)}>
            {leagues.map((entry) => <option key={entry} value={entry}>{entry === 'ALL' ? 'All Sports' : entry}</option>)}
          </select>
        </label>
        <label>
          Market
          <select value={market} onChange={(event) => setMarket(event.target.value)}>
            {(selectedGame?.markets || ['ML', 'SPREAD', 'TOTAL']).map((entry) => (
              <option key={entry} value={entry}>{MARKET_LABELS[entry] || entry}</option>
            ))}
          </select>
        </label>
        <label className="lmw-search-label">
          Find event
          <span className="lmw-search">
            <Search size={13} />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Team or matchup" />
          </span>
        </label>
        <label className="lmw-toggle">
          <input type="checkbox" checked={showAllLines} onChange={(event) => setShowAllLines(event.target.checked)} />
          Include alternate lines
        </label>
      </div>

      {error && <div className="lmw-error">{error}</div>}
      {catalog.proof?.closerOffers === 0 && (
        <div className="lmw-notice">Closing-line tape is not populated yet. Open, movement, and current prices remain independently available.</div>
      )}

      <div className="lmw-content">
        <aside className="lmw-games">
          <div className="lmw-games-title">Events · {visibleGames.length}</div>
          {loading && catalog.games.length === 0 ? (
            <div className="lmw-empty">Loading validated movement data...</div>
          ) : visibleGames.length === 0 ? (
            <div className="lmw-empty">No events match this filter.</div>
          ) : (
            <div className="lmw-game-list">
              {visibleGames.map((game) => (
                <button
                  key={game.id}
                  className={`lmw-game-btn ${selectedGameId === game.id ? 'active' : ''}`}
                  onClick={() => setSelectedGameId(game.id)}
                >
                  <span className="lmw-game-league">{game.league} · {game.bookCount} books</span>
                  <strong>{game.event}</strong>
                  <span>{game.date} · {game.time || 'time pending'}</span>
                </button>
              ))}
            </div>
          )}
        </aside>

        <section className="lmw-chart-area">
          {!selectedGame ? (
            <div className="lmw-chart-placeholder"><Search size={24} /><p>Select an event to inspect its price tape.</p></div>
          ) : visibleSeries.length === 0 ? (
            <div className="lmw-chart-placeholder"><p>No {MARKET_LABELS[market] || market} prices were captured for this event.</p></div>
          ) : (
            <>
              <div className="lmw-chart-title">
                <div><strong>{selectedGame.event}</strong><span>{selectedGame.league} · {selectedGame.date} · {MARKET_LABELS[market] || market}</span></div>
                <span>{visibleSeries.length} book/side series · implied probability</span>
              </div>
              <div className="lmw-canvas-wrap"><canvas ref={canvasRef} /></div>
              <div className="lmw-summary">
                <div className="lmw-summary-head">
                  <span>Book / side</span><span>Open</span><span>Now</span><span>Implied move</span><span>Close</span>
                </div>
                {visibleSeries.map((entry) => {
                  const movement = formatProbabilityMove(entry.openingOdds, entry.currentOdds)
                  return (
                    <div className="lmw-summary-row" key={entry.key} style={{ borderLeftColor: getBookBrandColor(entry.bookmaker) }}>
                      <span className="lmw-summary-book">
                        {getBookLogo(entry.bookmaker) && <img src={getBookLogo(entry.bookmaker)} alt="" onError={(event) => { event.target.style.display = 'none' }} />}
                        <span><strong>{entry.bookmaker}</strong><small>{entry.selection}{entry.line == null ? '' : ` ${entry.line > 0 ? '+' : ''}${entry.line}`}</small></span>
                      </span>
                      <span>{formatOdds(entry.openingOdds)}</span>
                      <strong>{formatOdds(entry.currentOdds)}</strong>
                      <span className={movement.value > 0 ? 'is-up' : movement.value < 0 ? 'is-down' : ''}>{movement.label}</span>
                      <span>{formatOdds(entry.closingOdds)}</span>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  )
}
